// Imports
import React, { useCallback, useEffect, useState } from 'react';
import GlobalStyle from '../shareables/foundation/simple-ui/components/global-style';
import Header from '../shareables/foundation/simple-ui/components/header';
import Footer from './resources/footer';
import Box from '../shareables/foundation/simple-ui/components/box';
import { useAppDispatch } from '../shareables/foundation/front-end/redux/hooks';
import { GetStartResponse } from '../shareables/types/api/main/start/get';
import apiActions from '../shareables/integrations/api/redux/actions';
import PageMessages from '../shareables/foundation/front-end/components/resources/page-messages';
import { GetSelfResponse } from '../shareables/types/api/main/self/get';
import * as Sentry from '@sentry/browser';
import thirdPartyIdentifiers from '../shareables/third-party-identifiers';
import commonActions from '../shareables/foundation/front-end/redux/actions';
import Steps from './steps';
import styled from 'styled-components/macro';
import ButtonGroup from '../shareables/foundation/front-end/components/buttons/group';
import Logo from '../shareables/front-end/svg-logo';
import { useSearchParams } from 'react-router-dom';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import { TrackAdClickParameters } from '../shareables/types/api/main/ad-click/track';


// Styled components
export const Content = styled(Box).attrs({ as: 'main' })`
	${ButtonGroup} {
		flex-direction: column;
		
		> button,
		> a {
			justify-content: center;
			margin-right: 0;
		}
	}
`;


// Define the accepted props
export type StepNumbers = 0 | 1 | 2 | 3 | 4 | 5;

interface Props {
	storybookStepNumber?: StepNumbers;
	storybookStartState?: GetStartResponse;
	storybookEmail?: string;
	storybookCommunityName?: string;
	storybookOnboardingCallScheduled?: boolean;
	storybookLoadingCompletion?: boolean;
	storybookCompleted?: boolean;
	storybookMigrate?: boolean;
	storybookContainsProtectedContent?: boolean;
}


// Function component
const App: React.FC<Props> = ({
	storybookStepNumber,
	storybookStartState,
	storybookEmail,
	storybookCommunityName,
	storybookOnboardingCallScheduled,
	storybookLoadingCompletion,
	storybookCompleted,
	storybookMigrate,
	storybookContainsProtectedContent,
}) => {
	// Use state
	const [stepNumber, setStepNumber] = useState<StepNumbers>(storybookStepNumber ?? 0);
	const [startState, setStartState] = useState<GetStartResponse | null>(storybookStartState ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const [searchParams, setSearchParams] = useSearchParams();
	const migrate = storybookMigrate ?? searchParams.get('migrate') === 'true';
	
	
	// Detect and track ad clicks
	useEffect(() => {
		void (async () => {
			// Extract UTM query parameters from URL
			const searchParams = new URLSearchParams(window.location.search);
			const utmCampaign = searchParams.get('utm_campaign');
			const trackingDetails = [...searchParams.entries()]
				.filter(([name]) => name.startsWith('utm_') && name !== 'utm_campaign')
				.map(([name, value]) => ({ name, value }));
			
			
			// Check for required parameter
			if (utmCampaign) {
				// Generate fingerprint
				let fingerprint: string;
				
				
				// Wrap in a try/catch
				try {
					fingerprint = await getFingerprint();
				} catch (error: unknown) {
					// Report to Sentry
					Sentry.captureException(error);
					
					
					// Return early
					return;
				}
				
				
				// Send ad click information to API
				void dispatch(
					apiActions.call<TrackAdClickParameters>({
						action: 'TRACK',
						uri: `/ad-click`,
						parameters: {
							trackingDetails: trackingDetails,
							campaign: utmCampaign,
							fingerprint: fingerprint,
						},
						completion: () => {
							// Do nothing
						},
					})
				);
			}
		})();
	}, [dispatch]);
	
	
	// Boot Intercom, update with user data when authenticated
	useEffect(() => {
		if (window.Intercom) {
			// Clear existing Intercom cache
			window.Intercom('shutdown');
			
			
			// Boot Intercom
			window.Intercom('boot', {
				app_id:
					process.env.REACT_APP__ENVIRONMENT === 'production'
						? thirdPartyIdentifiers.intercomAppID.production
						: thirdPartyIdentifiers.intercomAppID.development,
			});
		}
	}, []);
	
	
	// Function that loads self
	const memoizedLoadSelf = useCallback(
		() =>
			new Promise<void>((resolve) => {
				void dispatch(
					apiActions.call<undefined, undefined, GetSelfResponse>({
						action: 'GET',
						uri: '/self',
						callBeforeFinish: resolve,
						completion: (json) => {
							// Initialize variables for convenience
							const { intercom, ...user } = json.response;
							
							
							// Boot Intercom, if available
							if (window.Intercom) {
								window.Intercom('update', intercom);
							}
							
							
							// Store user for Sentry
							Sentry.configureScope((scope) => {
								scope.setUser({
									id: String(user.id),
									email: user.email,
								});
							});
							
							
							// Sign in
							dispatch(commonActions.signedIn(user));
						},
					})
				);
			}),
		[dispatch]
	);
	
	
	// Function that loads onboarding status
	const memoizedLoadStart = useCallback(
		() =>
			new Promise<void>((resolve) => {
				void dispatch(
					apiActions.call<undefined, undefined, GetStartResponse>({
						action: 'GET',
						uri: '/start',
						callBeforeFinish: resolve,
						completion: (json) => {
							const onboardStepToFrontendStepMap: Record<GetStartResponse['onboardStep'], StepNumbers> = {
								100: 0,
								101: 3,
								102: 4,
								110: 5,
							};
							
							setStartState((currentStartState) => {
								if (currentStartState === null && json.response.onboardStep > 100) {
									if (json.response.websiteID && !json.response.migration && migrate) {
										setSearchParams();
									}
									
									if (json.response.websiteID && json.response.migration && !migrate) {
										setSearchParams({ migrate: 'true' });
									}
									
									void memoizedLoadSelf();
								}
								
								return json.response;
							});
							
							setStepNumber(onboardStepToFrontendStepMap[json.response.onboardStep]);
						},
					})
				);
			}),
		[memoizedLoadSelf, dispatch, migrate, setSearchParams]
	);
	
	
	// Load onboarding status on mount
	useEffect(() => {
		if ((window as typeof window & { STORYBOOK?: true }).STORYBOOK === true) {
			return;
		}
		
		void memoizedLoadStart();
	}, [memoizedLoadStart]);
	
	
	// Return JSX
	return (
		<React.StrictMode>
			<GlobalStyle />
			
			{!(window as typeof window & { STORYBOOK?: true }).STORYBOOK && <PageMessages />}
			
			{startState && (
				<React.Fragment>
					<Header logoElement={<Logo />} title={`${migrate ? 'Migrate' : 'Start'} for free`} />
					
					<Content>
						<Steps
							stepNumber={stepNumber}
							setStepNumber={setStepNumber}
							startState={startState}
							memoizedLoadSelf={memoizedLoadSelf}
							memoizedLoadStart={memoizedLoadStart}
							migrate={migrate}
							storybookEmail={storybookEmail}
							storybookCommunityName={storybookCommunityName}
							storybookOnboardingCallScheduled={storybookOnboardingCallScheduled}
							storybookLoadingCompletion={storybookLoadingCompletion}
							storybookCompleted={storybookCompleted}
							storybookContainsProtectedContent={storybookContainsProtectedContent}
						/>
					</Content>
					
					<Footer />
				</React.Fragment>
			)}
		</React.StrictMode>
	);
};

export default App;
